import { useCallback, useEffect, useRef, useState } from 'react';
import { isArray, uniqBy } from 'lodash';
import { AppConfig } from '../../../Config';
import { Message } from '../components/CopilotMessage';

interface UseWebSocketChatProps {
  threadKey?: number;
  token: string | null;
  organizationId?: number;
  onNewMessage: () => void;
}

interface UseWebSocketChatReturn {
  ws: React.MutableRefObject<WebSocket | null>;
  isConnected: boolean;
  connectionError: string | null;
  aiConnectionStatus: 'connected' | 'disconnected' | 'processing' | 'idle';
  isProcessingPrompt: boolean;
  messagesByThread: Record<string, Message[]>;
  setProcessingPrompt: (processing: boolean) => void;
  setMessagesByThread: React.Dispatch<React.SetStateAction<Record<string, Message[]>>>;
  handleRoomChange: () => void;
  resetConnection: () => void;
}

export const useWebSocketChat = ({
  threadKey,
  token,
  organizationId,
  onNewMessage,
}: UseWebSocketChatProps): UseWebSocketChatReturn => {
  const ws = useRef<WebSocket | null>(null);
  const manualCloseRef = useRef(false);
  const [aiConnectionStatus, setAiConnectionStatus] = useState<
    'connected' | 'disconnected' | 'processing' | 'idle'
  >('disconnected');
  const [isConnected, setIsConnected] = useState(false);
  const [isProcessingPrompt, setProcessingPrompt] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [messagesByThread, setMessagesByThread] = useState<Record<string, Message[]>>({});

  const maxRetries = 5;
  const baseDelay = 1000;

  const handleRoomChange = useCallback(() => {
    let socket: WebSocket | null = null;
    let retryCount = 0;
    let retryTimeout: NodeJS.Timeout | null = null;
    let isConnecting = false;

    const mergeIncomingData = (eventData: any, threadKey: string) => {
      const data = JSON.parse(eventData);
      if (data) {
        // The consumer sends event["message"] directly, so data is already the message object
        // Handle both array and single message formats
        const toAppend = isArray(data)
          ? data.map((message: any) => ({
              id: message.id,
              status: message.status,
              role: message.role,
              message: message.message,
              error: message.error || false, // Ensure boolean, default to false
              actionResult: message.actionResult,
            }))
          : [
              {
                status: data.status,
                id: data.id,
                role: data.role,
                message: data.message,
                error: data.error || false, // Ensure boolean, default to false
                actionResult: data.actionResult,
              },
            ];

        // Stop processing when we receive a copilot response (role === 0)
        const lastMessage = isArray(data) ? data[data.length - 1] : data;
        if (lastMessage && lastMessage.role === 0 && !lastMessage.status) {
          setProcessingPrompt(false);
          onNewMessage();
        }

        setMessagesByThread(prev => {
          const currentMessages = prev[threadKey] || [];
          const filteredCurrentMessages = currentMessages.filter(existing =>
            !(
              typeof existing.id === 'number' &&
              existing.id < 0 &&
              toAppend.some((incoming: any) =>
                incoming.role === existing.role && incoming.message === existing.message
              )
            )
          );
          return {
            ...prev,
            [threadKey]: uniqBy([...filteredCurrentMessages, ...toAppend], 'id'),
          };
        });
      }
    };

    const connect = (threadKey: string) => {
      if (!token || isConnecting) {
        return;
      }
      isConnecting = true;

      // Construct WebSocket URL with proper protocol
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const backendHost =
        AppConfig.api.wsApiUrl ||
        (process.env.NODE_ENV === 'development' ? 'localhost:8000' : window.location.host);
      // If wsApiUrl already includes protocol, use it as-is; otherwise construct it
      const wsUrl =
        backendHost.startsWith('ws://') || backendHost.startsWith('wss://')
          ? `${backendHost}/ws/chat/${encodeURIComponent(threadKey)}/`
          : `${protocol}//${backendHost}/ws/chat/${encodeURIComponent(threadKey)}/`;

      socket = new WebSocket(wsUrl);
      ws.current = socket;

      socket.onopen = () => {
        manualCloseRef.current = false;
        if (socket) {
          setAiConnectionStatus('disconnected'); // Will be updated when status message arrives
          socket.send(
            JSON.stringify({
              type: 'authentication',
              token: `Bearer ${token}`,
              organization_id: organizationId,
            })
          );
        }
        setIsConnected(true);
        setConnectionError(null);
        retryCount = 0;
        isConnecting = false;
      };

      socket.onmessage = event => {
        try {
          const data = JSON.parse(event.data);

          // Check for connection-level errors (not message errors)
          if (data.error && !data.role && !data.message) {
            console.error('WebSocket connection error:', data.error);
            setConnectionError(data.error);
            setIsConnected(false);
            return;
          }

          // Handle status messages separately
          if (data.status) {
            if (data.status === 'connected') {
              setAiConnectionStatus('idle');
            } else if (data.status === 'disconnected') {
              setAiConnectionStatus('disconnected');
            } else if (data.status === 'processing_start') {
              setAiConnectionStatus('processing');
              setProcessingPrompt(true);
            } else if (data.status === 'processing_end') {
              setAiConnectionStatus('idle');
              setProcessingPrompt(false);
            }
            // Don't add status messages to the message list
            return;
          }

          // Handle both array and single message formats
          if (Array.isArray(data)) {
            mergeIncomingData(event.data, threadKey);
            return;
          }

          // Single message - merge it
          mergeIncomingData(event.data, threadKey);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      socket.onclose = () => {
        setIsConnected(false);
        isConnecting = false;
        if (!manualCloseRef.current && threadKey) {
          retryConnection(threadKey.toString());
        }
      };

      socket.onerror = error => {
        console.error('WebSocket error:', error);
        isConnecting = false;
      };
    };

    const retryConnection = (threadKey: string) => {
      if (retryCount < maxRetries) {
        const delay = Math.min(baseDelay * 2 ** retryCount, 30000);
        retryCount++;
        retryTimeout = setTimeout(() => connect(threadKey), delay) as any;
      } else {
        setConnectionError(
          'Failed to connect after multiple attempts. Please try refreshing the page.'
        );
      }
    };

    if (
      ws.current &&
      ws.current.readyState === WebSocket.OPEN &&
      ws.current.url.includes(threadKey?.toString() || 'noId')
    ) {
      return;
    }

    if (threadKey) {
      connect(threadKey.toString());
    }

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      if (socket) {
        socket.close();
      }
    };
  }, [threadKey, token, organizationId, onNewMessage]);

  const resetConnection = useCallback(() => {
    if (ws.current) {
      manualCloseRef.current = true;
      ws.current.onclose = null;
      ws.current.close();
      ws.current = null;
    }
    setIsConnected(false);
    setAiConnectionStatus('disconnected');
    setProcessingPrompt(false);
    setConnectionError(null);
  }, []);

  return {
    ws,
    isConnected,
    connectionError,
    aiConnectionStatus,
    isProcessingPrompt,
    messagesByThread,
    setProcessingPrompt,
    setMessagesByThread,
    handleRoomChange,
    resetConnection,
  };
};


